import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Checkbox } from 'ui-components/components/checkbox';
import { emptyArray } from 'ui-components/default-props/empty';

import { SELECT_ALL } from '../../../constants';
import { tableMessages } from '../../messages';

import type { CarrierPackage } from '../carrier-packages-table.d';
import type { ColumnInstance } from 'react-table';

type CheckboxColumnInstance = ColumnInstance & {
  data?: CarrierPackage[];
  onSelectionChange?: (key: string, isSelected: boolean) => void;
};

type CheckboxHeaderProps = {
  column: CheckboxColumnInstance;
};

type CheckboxCellProps = {
  column: CheckboxColumnInstance;
  row: {
    original: CarrierPackage;
  };
};

export const CheckboxHeader = ({ column }: CheckboxHeaderProps) => {
  const intl = useIntl();
  const data = column.data ?? emptyArray;
  const { onSelectionChange } = column;

  const allSelected = useMemo(() => data.length > 0 && data.every((pkg: CarrierPackage) => pkg.isSelected), [data]);
  const noneSelected = useMemo(() => data.length === 0 || data.every((pkg: CarrierPackage) => !pkg.isSelected), [data]);
  const someSelected = !allSelected && !noneSelected;
  const anySelected = !noneSelected;
  const ariaLabel = noneSelected
    ? intl.formatMessage(tableMessages.ariaLabels.selectAllPackages)
    : intl.formatMessage(tableMessages.ariaLabels.deselectAllPackages);

  const handleChange = () => {
    if (onSelectionChange) {
      onSelectionChange(SELECT_ALL, anySelected);
    }
  };

  return <Checkbox ariaLabel={ariaLabel} checked={allSelected} indeterminate={someSelected} onChange={handleChange} />;
};

export const CheckboxCell = ({ row, column }: CheckboxCellProps) => {
  const intl = useIntl();
  const { onSelectionChange } = column;
  const { name, packageTypeId, isSelected } = row.original;
  const ariaLabel = isSelected
    ? intl.formatMessage(tableMessages.ariaLabels.deselectPackage, { packageName: name })
    : intl.formatMessage(tableMessages.ariaLabels.selectPackage, { packageName: name });

  const handleChange = () => {
    if (onSelectionChange) {
      onSelectionChange(packageTypeId, isSelected);
    }
  };

  return <Checkbox ariaLabel={ariaLabel} checked={isSelected} onChange={handleChange} />;
};
